/**
 * CapacityWeekCell.tsx
 * ONE (employee, week) TILE in the "Team Allocation & Weekly Capacity" grid
 * (STAFF-VIEW-V2 cell redesign 2026-07-14). The body is a horizontal split that
 * separates the two distinct meanings the old stacked twin bars conflated:
 *   LEFT  — a small load RING (CapacityLoadRing): the person's Σ TOTAL weekly
 *           load across all plans, colored by band (under/at/over), % centered.
 *           READ-ONLY — the glanceable "overall load" signal.
 *   RIGHT — the editable THIS-PLAN allocation as a prominent number + −/+
 *           steppers (AllocationStepper variant='number', no bar). Locked plans
 *           show the number read-only, no steppers.
 * Distinct shapes (ring vs number) → no more two-identical-bars confusion.
 * The day-by-day work is still revealed by CLICKING the tile to expand.
 *
 * The day-off / conflict signal sits on the tile: a task-on-a-day-off is the one
 * hard conflict → a red inset ring + tint; a plain day off (absence, no task on
 * it) → a muted "day off" note. Over-100% never blocks (D4) — the ring reads red.
 *
 * Presentational only: the parent owns the debounce + the PUT + the expand
 * fetch. Editing the stepper stops propagation so it never toggles the expand.
 * Colors via theme tokens — no hex literals (CODING-STANDARDS).
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { alpha, useTheme } from '@mui/material/styles';
import { CapacityLoadRing } from './CapacityLoadRing';
import { AllocationStepper } from './AllocationStepper';
import { allocationBand } from '../../../../utils/scheduleAllocationBands';

export interface CapacityWeekCellProps {
  /** Σ TOTAL utilization % (this plan + other plans) → drives the band color. */
  allocationPercent: number | undefined;
  /** Secondary line under the band, e.g. "busy 3 of 5 days" (optional). */
  subLabel?: string | null;
  /** THIS plan's source allocation % for the (person, week) → the stepper value. */
  sourcePercent: number;
  /** Show the editable stepper (hidden / omitted when the plan is locked). */
  editable: boolean;
  /** Hard conflict — a task assigned on a day this person has off (was the red day cell). */
  hasConflict: boolean;
  /** This person has a day off (absence) somewhere in the week (informational). */
  hasDayOff: boolean;
  /** The tile's week detail is currently expanded below. */
  expanded: boolean;
  /** Toggle the inline day-by-day breakdown for this (person, week). */
  onToggleExpand: () => void;
  /** Commit a new source allocation % for this (person, week). */
  onSetPercent: (percent: number) => void;
}

export const CapacityWeekCell: React.FC<CapacityWeekCellProps> = ({
  allocationPercent,
  subLabel,
  sourcePercent,
  editable,
  hasConflict,
  hasDayOff,
  expanded,
  onToggleExpand,
  onSetPercent,
}) => {
  const theme = useTheme();
  const band = allocationBand(allocationPercent);

  // Tile framing: conflict = red ring + faint red tint (the one hard signal);
  // expanded = sage selection ring; otherwise a soft sage tile.
  const ring = hasConflict
    ? `inset 0 0 0 2px ${theme.schedule.heatmapHigh}`
    : expanded
      ? `inset 0 0 0 2px ${theme.schedule.selection}`
      : `inset 0 0 0 1px ${theme.border.color}`;
  const tileBg = hasConflict
    ? alpha(theme.schedule.heatmapHigh, 0.06)
    : theme.schedule.selectionBg;

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={onToggleExpand}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleExpand(); }
      }}
      sx={{
        mx: 0.5,
        my: 0.4,
        borderRadius: `${theme.border.radius}px`,
        backgroundColor: tileBg,
        boxShadow: ring,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.15,
        transition: 'box-shadow 0.12s, background-color 0.12s',
        '&:hover': { backgroundColor: alpha(theme.schedule.selection, 0.18) },
      }}
    >
      {/* Split body: LEFT load ring (total, read-only) · RIGHT this-plan %. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, pt: 0.5, pb: 0.25 }}>
        {/* LEFT (~28%): total-load ring — the glanceable RESULT (all jobs). The
            "total" caption mirrors the right column's "this job" so a full ring
            beside "0% this job" reads as "busy elsewhere", not a glitch. */}
        <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.1 }}>
          <CapacityLoadRing percent={allocationPercent} band={band} subLabel={subLabel} thisJobPercent={sourcePercent} />
          <Typography variant="caption" sx={{ fontSize: '0.52rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            total · all jobs
          </Typography>
        </Box>

        {/* RIGHT (~72%): this-plan allocation — the editable INPUT (or read-only). */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.1 }}>
          {editable ? (
            <Box
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{ width: '100%' }}
            >
              <AllocationStepper percent={sourcePercent} onChange={onSetPercent} variant="number" />
            </Box>
          ) : (
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, lineHeight: 1, color: theme.schedule.employeeAlloc }}>
              {Math.round(sourcePercent)}%
            </Typography>
          )}
          <Typography variant="caption" sx={{ fontSize: '0.52rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            this job
          </Typography>
        </Box>
      </Box>

      {/* Day-off / conflict signal — moved here from the retired per-day cells. */}
      {(hasConflict || hasDayOff) && (
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.56rem',
            fontWeight: 700,
            textAlign: 'center',
            lineHeight: 1,
            color: hasConflict ? theme.schedule.heatmapHigh : theme.palette.text.secondary,
          }}
        >
          {hasConflict ? 'day-off conflict' : 'day off'}
        </Typography>
      )}

      {/* Expand affordance — reveals the day-by-day WO/task breakdown. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, pb: 0.3, color: 'text.disabled' }}>
        {expanded ? <ExpandMoreIcon sx={{ fontSize: 13 }} /> : <ChevronRightIcon sx={{ fontSize: 13 }} />}
        <Typography variant="caption" sx={{ fontSize: '0.55rem', fontWeight: 600 }}>
          {expanded ? 'hide days' : 'show days'}
        </Typography>
      </Box>
    </Box>
  );
};
